import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainerReactTable from '../../../../../Components/Common/TableContainerReactTable';
import classnames from 'classnames';
import FormInput from '../../common/FormInput';
import SelectInput from '../../common/SelectInput';
import { useLazyGetVendorsByVendorNameQuery, VendorData } from 'features/organizations';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';


export interface VendorSearchForm {
  searchField: string;
  searchValue: string;
}

interface AssociateVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (vendor: VendorData) => void;
}

const defaultSearchForm: VendorSearchForm = {
  searchField: 'Name',
  searchValue: ''
};


const searchFieldOptions = ['Name', 'Location', 'Type', 'Industry'];

const AssociateVendorModal: React.FC<AssociateVendorModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelect 
}) => {
  const [searchForm, setSearchForm] = useState<VendorSearchForm>(defaultSearchForm);
  const [selectedVendor, setSelectedVendor] = useState<VendorData | null>(null);
  const [activeTab] = useState('1'); // Only one tab, but keep for extensibility

  const handleSearchFieldChange = (field: keyof VendorSearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  //Redux query
  const [getVendorsByVendorName, { data: vendorsData, isLoading: isVendorsLoading }] = useLazyGetVendorsByVendorNameQuery();

  const handleSearch = async () => {
    try {
      // Use '%' as default when search value is empty to get all results
      const searchParam = searchForm.searchValue.trim() || '%';
      await getVendorsByVendorName({ vendorName: searchParam });
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setSelectedVendor(null);
  };

  const handleRowClick = (vendor: VendorData) => {
    setSelectedVendor(vendor);
    // console.log('Vendor selected:', vendor);
    // Immediately select the vendor when row is clicked and close modal
    if (onSelect) {
      onSelect(vendor);
    }
    onClose(); // Close modal after selection
  };

  const handleRowDoubleClick = (vendor: VendorData) => {
    if (onSelect) {
      onSelect(vendor);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedVendor(null);
    onClose();
  };


  const handleClears = () => {
    setSelectedVendor(null);
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedVendor?.rowId === cell.row.original.rowId ? 'table-active' : ''}
        >
          <span className="text-link" style={{ textDecoration: 'underline' }}>
            {cell.getValue()}
          </span>
        </div>
      )
    },
    {
      header: 'Location',
      accessorKey: 'location',
      enableColumnFilter: false
    },
    {
      header: 'Type',
      accessorKey: 'type',
      enableColumnFilter: false
    },
    {
      header: 'Industry',
      accessorKey: 'industryName',
      enableColumnFilter: false
    },
    {
      header: "Active",
      accessorKey: "activeFlag",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const isActive = cell.getValue();
        return isActive ? (
          <div className="text-start ps-1">
            <i className="ri-checkbox-circle-line fs-17 align-middle"></i>
          </div>
        ) : null;
      },
    },
  ], [selectedVendor]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Associate Vendor</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => {}}
              onKeyDown={() => {}}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Form>
              {/* Removed the Search section label row for a cleaner look */}
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className='me-3'>
                  <button
                    type="button"
                    onClick={() => handleSearch()}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1} className=''>
                  <button
                    type="button"
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Vendor Table */}
              <Row>
                <Col xs={12}>
                  {isVendorsLoading ? (
                    <LoadingIndicator
                      isLoading={isVendorsLoading}
                      isProcessing={false}
                      error={null}
                      title="Loading vendors from server..."
                      height="200px"
                    />
                  ) : (
                    <TableContainerReactTable
                      columns={columns}
                      data={vendorsData?.data || []}
                      isGlobalFilter={false}
                      customPageSize={10}
                      divClass="mb-0"
                      tableClass="table-centered align-middle table-nowrap mb-0"
                      theadClass="table-light"
                      thClass="px-3 py-2"
                      verticalLines={true}
                    />
                  )}
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
          <button
            onClick={handleClears}
            className="btn btn-soft-warning btn-sm"
            style={{ minWidth: 80 }}
          >
            Clears
          </button>
          <button
            className="btn btn-soft-dark btn-sm"
            onClick={handleCancel}
            style={{ minWidth: 80 }}
          >
            Cancel
          </button>
      </ModalFooter>
    </Modal>
  );
};

export default AssociateVendorModal;
